/**
 * MF-CUSTOM (Empat, 4.1.1): <mf-shipping-progress>, the visual track inside <free-shipping-bar>
 * (snippets/free-shipping-bar.liquid). Styled by the theme's .progress-bar CSS, which draws the fill
 * from a --progress custom property (0-1) with a transform transition.
 *
 * Why a custom element instead of the theme's <progress-bar>: the cart drawer replaces its whole
 * content on every cart change (see replaceContent_fn in theme.js), so the bar was re-created each time
 * already at the new value, and nothing ever animated. This element remembers the last value it showed
 * (module-level, so it survives those re-renders), starts a new instance at that value and then moves
 * to the new one on the next frame, so the CSS transition actually plays. On first display it fills
 * from 0 once it scrolls or slides into view.
 *
 * The value comes from the parent <free-shipping-bar>'s "total-price"/"threshold" attributes (both in
 * cents once that element is upgraded), which theme.js keeps current on every cart change, so the cart
 * total isn't re-derived here. See sections/cart-drawer.liquid, sections/main-cart.liquid and
 * CUSTOM-CHANGES.md.
 */
let lastProgress = null;

class MfShippingProgress extends HTMLElement {
  async connectedCallback() {
    this.bar = this.closest('free-shipping-bar');
    if (!this.bar) return;

    await customElements.whenDefined('free-shipping-bar');
    if (!this.isConnected) return;

    this.attributeObserver = new MutationObserver(() => this.update());
    this.attributeObserver.observe(this.bar, { attributes: true, attributeFilter: ['total-price', 'threshold'] });

    if (lastProgress === null) {
      this.style.setProperty('--progress', '0');
      this.visibilityObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.visibilityObserver.disconnect();
          this.nextFrame(() => this.update());
        }
      });
      this.visibilityObserver.observe(this);
    } else {
      this.style.setProperty('--progress', `${lastProgress}`);
      this.nextFrame(() => this.update());
    }
  }

  disconnectedCallback() {
    this.attributeObserver?.disconnect();
    this.visibilityObserver?.disconnect();
  }

  get progress() {
    const threshold = parseFloat(this.bar.getAttribute('threshold')) || 0;
    const totalPrice = parseFloat(this.bar.getAttribute('total-price')) || 0;

    return threshold > 0 ? Math.min(1, totalPrice / threshold) : 1;
  }

  update() {
    const threshold = parseFloat(this.bar.getAttribute('threshold')) || 0;
    const totalPrice = parseFloat(this.bar.getAttribute('total-price')) || 0;
    const progress = this.progress;

    this.style.setProperty('--progress', `${progress}`);
    this.setAttribute('aria-valuemax', threshold);
    this.setAttribute('aria-valuenow', Math.min(totalPrice, threshold));
    this.bar.classList.toggle('free-shipping-bar--reached', progress >= 1);
    lastProgress = progress;
  }

  // two frames so the starting value is painted before the target is set (otherwise no transition)
  nextFrame(callback) {
    requestAnimationFrame(() => requestAnimationFrame(callback));
  }
}

if (!customElements.get('mf-shipping-progress')) {
  customElements.define('mf-shipping-progress', MfShippingProgress);
}
